// ═══════════════════════════════════════════════════════════
// Payment Tokens — per-invoice secure payment links
//
// Reuses the uploadTokens table (label = 'payment:{invoiceId}')
// to avoid adding a new GAB table. Staff generates a token when
// they click "Send Payment Link". The portal /pay/:token page
// validates it and processes the (stubbed) payment.
// ═══════════════════════════════════════════════════════════

import { queryRecords, updateRecord } from './client';
import { createUploadToken } from './uploadTokens';
import { TABLES } from './tables';
import { FIELDS } from './fields';

const F = FIELDS.uploadTokens;

export interface PaymentToken {
  recordId:  number;
  requestId: number;
  invoiceId: number;
  token:     string;
  expiresAt: string;
}

function parseInvoiceId(label: string): number | null {
  const m = label.match(/^payment:(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

function rowToPaymentToken(row: Record<string, unknown>): PaymentToken | null {
  const label = String(row[F.label] ?? '');
  const invoiceId = parseInvoiceId(label);
  if (invoiceId === null) return null;
  return {
    recordId:  row['record_id'] as number,
    requestId: Number(row[F.requestId] ?? 0),
    invoiceId,
    token:     String(row[F.token] ?? ''),
    expiresAt: String(row[F.expiresAt] ?? ''),
  };
}

/**
 * Create (or replace) a payment token for an invoice.
 * Revokes any existing payment tokens for the same request first.
 */
export async function createPaymentToken(
  requestId: number,
  invoiceId: number,
  actorEmail: string,
): Promise<PaymentToken> {
  // Revoke any existing payment tokens for this request
  const { records } = await queryRecords(TABLES.uploadTokens, {
    filters: [
      { field: F.requestId, operator: 'eq', value: requestId },
      { field: F.isActive,  operator: 'eq', value: true },
    ],
    limit: 50,
  });

  const existing = (records as Record<string, unknown>[])
    .filter(r => parseInvoiceId(String(r[F.label] ?? '')) !== null);

  for (const r of existing) {
    await updateRecord(TABLES.uploadTokens, r['record_id'] as number, {
      [F.isActive]: false,
    });
  }

  const token = await createUploadToken({
    requestId,
    label:      `payment:${invoiceId}`,
    expiryDays: 30,
    createdBy:  actorEmail,
  });

  return {
    recordId:  token.recordId,
    requestId: token.requestId,
    invoiceId,
    token:     token.token,
    expiresAt: token.expiresAt,
  };
}

/**
 * Look up the active payment token for a specific invoice.
 */
export async function getPaymentTokenForInvoice(
  requestId: number,
  invoiceId: number,
  opts?: { anonymous?: boolean },
): Promise<PaymentToken | null> {
  const label = `payment:${invoiceId}`;
  const { records } = await queryRecords(TABLES.uploadTokens, {
    filters: [
      { field: F.requestId, operator: 'eq',    value: requestId },
      { field: F.label,     operator: 'eq',    value: label     },
      { field: F.isActive,  operator: 'eq',    value: true      },
    ],
    limit: 5,
  }, opts);

  const row = (records as Record<string, unknown>[])[0];
  if (!row) return null;
  const parsed = rowToPaymentToken(row);
  if (!parsed) return null;
  if (new Date(parsed.expiresAt) < new Date()) return null;
  return parsed;
}

/**
 * Look up an existing active payment token for a request (any invoice).
 * Pass { anonymous: true } when calling from the public portal.
 */
export async function getPaymentTokenForRequest(
  requestId: number,
  opts?: { anonymous?: boolean },
): Promise<PaymentToken | null> {
  const { records } = await queryRecords(TABLES.uploadTokens, {
    filters: [
      { field: F.requestId, operator: 'eq', value: requestId },
      { field: F.isActive,  operator: 'eq', value: true },
    ],
    limit: 10,
  }, opts);

  const row = (records as Record<string, unknown>[])
    .find(r => parseInvoiceId(String(r[F.label] ?? '')) !== null);

  if (!row) return null;
  const parsed = rowToPaymentToken(row);
  if (!parsed) return null;
  if (new Date(parsed.expiresAt) < new Date()) return null;
  return parsed;
}

/**
 * Validate a payment token from the public portal (anonymous).
 * Returns token metadata including invoiceId and requestId, or null if invalid.
 */
export async function validatePaymentToken(tokenStr: string): Promise<PaymentToken | null> {
  const { records } = await queryRecords(TABLES.uploadTokens, {
    filters: [
      { field: F.token,    operator: 'eq', value: tokenStr },
      { field: F.isActive, operator: 'eq', value: true },
    ],
    limit: 1,
  }, { anonymous: true });

  if (!records.length) return null;
  const row = records[0] as Record<string, unknown>;
  const parsed = rowToPaymentToken(row);
  if (!parsed) return null;
  if (new Date(parsed.expiresAt) < new Date()) return null;
  return parsed;
}

/** Invalidate a payment token after successful payment. */
export async function consumePaymentToken(recordId: number): Promise<void> {
  await updateRecord(TABLES.uploadTokens, recordId, {
    [F.isActive]: false,
  }, { anonymous: true });
}

/** Build the public-facing payment URL. */
export function buildPaymentUrl(token: string): string {
  return `${window.location.origin}/portal/pay/${token}`;
}
